import React from "react";
import styled, { keyframes } from "styled-components/macro";

const ProductCardSkeleton: React.FC = () => (
  <SkeletonCard data-testid="product-card-skeleton" aria-hidden="true">
    <MediaSkeleton>
      <BadgeSkeleton />
    </MediaSkeleton>

    <DetailsSkeleton>
      <TitleSkeleton />
      <ShortTitleSkeleton />
      <ChipsSkeleton>
        <ChipSkeleton />
        <SmallChipSkeleton />
        <SmallChipSkeleton />
      </ChipsSkeleton>
    </DetailsSkeleton>

    <FooterSkeleton>
      <PriceRow>
        <PriceSkeleton />
        <StockSkeleton />
      </PriceRow>
      <ActionsSkeleton>
        <AddButtonSkeleton />
        <CompareButtonSkeleton />
      </ActionsSkeleton>
    </FooterSkeleton>
  </SkeletonCard>
);

const shimmer = keyframes`
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
`;

const SkeletonBlock = styled.div`
  background: linear-gradient(90deg, #edf1f3 24%, #f8fafb 38%, #edf1f3 62%);
  background-size: 300% 100%;
  animation: ${shimmer} 1.35s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    background: #edf1f3;
  }
`;

const SkeletonCard = styled.article`
  box-sizing: border-box;
  display: flex;
  width: 100%;
  min-width: 0;
  height: 100%;
  min-height: 420px;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #e6edf2;
  border-radius: 14px;
  box-shadow: 0 3px 14px rgba(25, 25, 25, 0.05);
`;

const MediaSkeleton = styled(SkeletonBlock)`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
`;

const BadgeSkeleton = styled(SkeletonBlock)`
  position: absolute;
  top: 12px;
  left: 12px;
  width: 76px;
  height: 27px;
  border-radius: 6px;
`;

const DetailsSkeleton = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 16px;
`;

const TitleSkeleton = styled(SkeletonBlock)`
  width: 90%;
  height: 15px;
  border-radius: 5px;
`;

const ShortTitleSkeleton = styled(SkeletonBlock)`
  width: 64%;
  height: 15px;
  margin-top: 9px;
  border-radius: 5px;
`;

const ChipsSkeleton = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: auto;
  padding-top: 18px;
`;

const ChipSkeleton = styled(SkeletonBlock)`
  width: 72px;
  height: 26px;
  border-radius: 999px;
`;

const SmallChipSkeleton = styled(ChipSkeleton)`
  width: 48px;
`;

const FooterSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 16px 16px;
  border-top: 1px solid #edf1f4;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
`;

const PriceSkeleton = styled(SkeletonBlock)`
  width: 92px;
  height: 24px;
  border-radius: 6px;
`;

const StockSkeleton = styled(SkeletonBlock)`
  width: 72px;
  height: 12px;
  border-radius: 4px;
`;

const ActionsSkeleton = styled.div`
  display: flex;
  gap: 8px;
`;

const AddButtonSkeleton = styled(SkeletonBlock)`
  height: 46px;
  flex: 1;
  border-radius: 9px;
`;

const CompareButtonSkeleton = styled(SkeletonBlock)`
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  border-radius: 9px;
`;

export default ProductCardSkeleton;
